import { addHours, differenceInMinutes, format, isBefore } from "date-fns";
import pluralize from "pluralize";
import React, { createContext, Suspense, useContext } from "react";

import {
  Column,
  Container,
  cssVariables,
  Hoverable,
  Icon,
  Image,
  Popover,
  Pressable,
  Row,
  Spacing,
  Text,
} from "../../components";
import { Booking, BookingType, getTimeSlot } from "../../data";
import {
  AvailabilitySlot,
  BlockOutDates,
  useGetSpaceBlockOutDates,
  useGetSpaceBookings,
  useGetSpaceCalendar,
  useGetSpacePhysicalSpaces,
} from "../../data/api";
import { BookingHour, getMaxCapacityLabel } from "../../data/space";
import { useToggle } from "../../hooks";
import {
  datesInInterval,
  parseDay,
  parseTime,
  setTime,
  toDay,
} from "../../lib/datetime";
import { useAuth0 } from "../../core/auth0";
import { BookingPreview } from "./booking_preview";
import { useFMSettingNewOrderByPartner } from "../../core/splitio";
import { Space } from "../data/space";

interface ManagerSpaceListViewProps {
  spaces: Space[];
  date: Date;
  scrollToBookingID?: string;
}

const ListViewContext = createContext({
  endOfDay: new Date(),
  startOfDay: new Date(),
  totalDay: 0,
  hours: [] as Date[],
});

/**
 * Displays its children in a horizontal array.
 */
export function ManagerSpaceListView(props: ManagerSpaceListViewProps) {
  const { date, spaces, scrollToBookingID } = props;
  const dayOfWeek = date.getDay();

  const bookingHoursOnDate: BookingHour[] = [];
  spaces.forEach((s) => {
    const bookingHour = s.bookingHours.find((bh) => bh.dayOfWeek === dayOfWeek);

    if (bookingHour) {
      bookingHoursOnDate.push(bookingHour);
    }
  });

  let startOfDay = parseTime("0600");

  bookingHoursOnDate.forEach((b) => {
    const openAt = parseTime(b.openAt);

    if (isBefore(openAt, startOfDay)) {
      startOfDay = openAt;
    }
  });

  const endOfDay = setTime(new Date(), 23, 0, 0, 1);
  const totalDay = differenceInMinutes(endOfDay, startOfDay) + 60;
  const hours = datesInInterval({ start: startOfDay, end: endOfDay }, addHours);

  return (
    <ListViewContext.Provider value={{ totalDay, hours, startOfDay, endOfDay }}>
      <Container borderWidth={1} borderColor="transparent">
        <Row alignItems="center">
          <Container width={320}>
            <Text isBold fontSize="lg">
              All rooms
            </Text>
          </Container>
          <Spacing width={16} />
        </Row>
      </Container>
      <Spacing height={16} />
      <Column>
        {spaces.map((space) => (
          <React.Fragment key={space.id}>
            <SpaceListItem
              date={date}
              space={space}
              scrollToBookingID={scrollToBookingID}
            />
            <Spacing height={16} />
          </React.Fragment>
        ))}
      </Column>
    </ListViewContext.Provider>
  );
}

interface SpaceListItemProps {
  space: Space;
  date: Date;
  scrollToBookingID?: string;
}

function SpaceListItem(props: SpaceListItemProps) {
  const { space, date, scrollToBookingID } = props;
  const dateRange = {
    startDate: toDay(date),
    endDate: toDay(date),
  };
  const bookings = useGetSpaceBookings(space.id, dateRange);
  const [expanded, setExpanded] = React.useState(
    scrollToBookingID && bookings.some((b) => b.id === scrollToBookingID)
  );
  const blockOutDatesList = useGetSpaceBlockOutDates(space.id, dateRange);
  let blockOutDates: BlockOutDates | null = null;

  if (blockOutDatesList.length > 0) {
    blockOutDates = blockOutDatesList[0];
  }
  const physicalSpaces = useGetSpacePhysicalSpaces(space.id);

  const calendar = useGetSpaceCalendar(space.id, {
    date: toDay(date),
  });

  const hasMore = physicalSpaces.length > 3;
  const moreSpaces = physicalSpaces.length - 3;

  if (physicalSpaces.length === 0) {
    return null;
  }

  return (
    <Container
      flex={1}
      padding={16}
      borderWidth={1}
      borderColor={cssVariables.borderColorDefault}
      borderRadius={cssVariables.radius}
    >
      <Row>
        <Container width={320 - 16} height={128}>
          <Row>
            {space.images[0] && (
              <Image
                width={100}
                height={100}
                objectFit="cover"
                src={space.images[0].small.url}
                alt={`${space.name} thumbnail`}
                borderRadius={cssVariables.radius}
              />
            )}
            <Spacing width={8} />
            <Column>
              <Text isBold>{space.name}</Text>
              <Text fontSize="sm">{getMaxCapacityLabel(space)}</Text>
            </Column>
          </Row>
        </Container>
        <Container flex={1}>
          <Column expanded>
            {physicalSpaces.slice(0, expanded ? undefined : 3).map((ps) => {
              const physicalSpaceCalendar = calendar.physicalSpaces.find(
                (p) => p.physicalSpaceID === ps.id
              );

              if (!physicalSpaceCalendar) {
                throw new Error(
                  `physical space calendar not found with id=${ps.id}`
                );
              }

              const physicalSpaceBookings = bookings.filter(
                (b) => b.physicalSpaceID === ps.id
              );

              return (
                <React.Fragment key={ps.id}>
                  {physicalSpaces.length > 1 && <Text>{ps.room}</Text>}
                  <Timeline
                    bookings={physicalSpaceBookings}
                    scrollToBookingID={scrollToBookingID}
                    blockOutDates={blockOutDates}
                    open={calendar.open}
                    availabilities={physicalSpaceCalendar.availabilities}
                  />

                  <Spacing height={24} />
                </React.Fragment>
              );
            })}
          </Column>
          {hasMore && (
            <>
              {expanded ? (
                <Pressable onPress={() => setExpanded(false)}>
                  <Row alignItems="center">
                    <Text color="primary">
                      {moreSpaces} More {pluralize("space", moreSpaces)}
                    </Text>
                    <Spacing width={4} />
                    <Icon name="chevron-up" />
                  </Row>
                </Pressable>
              ) : (
                <Pressable onPress={() => setExpanded(true)}>
                  <Row alignItems="center">
                    <Text isBold>
                      {moreSpaces} More {pluralize("space", moreSpaces)}
                    </Text>
                    <Spacing width={4} />
                    <Icon name="chevron-down" />
                  </Row>
                </Pressable>
              )}
            </>
          )}
        </Container>
      </Row>
    </Container>
  );
}

interface TimelineProps {
  scrollToBookingID?: string;
  blockOutDates: BlockOutDates | null;
  bookings: Booking[];
  availabilities: AvailabilitySlot[];
  open: boolean;
}

function Timeline(props: TimelineProps) {
  const { bookings, blockOutDates, open, scrollToBookingID, availabilities } =
    props;
  const { hours } = useContext(ListViewContext);

  return (
    <Container>
      <Container>
        <Row alignItems="center">
          {hours.map((h) => (
            <Container
              key={h.toISOString()}
              flex={1}
              borderRightWidth={1}
              borderColor="transparent"
            >
              <Text color="muted" fontSize="xs">
                {format(h, "ha")}
              </Text>
            </Container>
          ))}
        </Row>
      </Container>
      <Container
        height={104}
        width="100%"
        borderLeftWidth={1}
        borderTopWidth={1}
        borderBottomWidth={1}
        borderRadius={cssVariables.radius}
        borderColor={cssVariables.borderColorDark}
      >
        <Row alignItems="center" expanded>
          {hours.map((hour) => {
            const timeSlot = getTimeSlot(
              hour,
              open,
              availabilities,
              blockOutDates
            );

            let content: React.ReactNode = null;

            if (timeSlot.type === "available") {
              content = <AvailableTimeSlot />;
            } else if (timeSlot.type === "blocked_out_dates") {
              content = (
                <BlockedOutTimeSlot
                  blockedOutDates={timeSlot.blockedOutDates}
                />
              );
            } else {
              content = (
                <UnavailableTimeSlot
                  reason={timeSlot.description}
                  shaded={timeSlot.type !== "past_available"}
                />
              );
            }

            return (
              <Container
                key={hour.toISOString()}
                flex={1}
                borderRightWidth={1}
                borderColor={cssVariables.borderColorDark}
                expanded
              >
                {content}
              </Container>
            );
          })}
          {bookings.map((b) => (
            <TimelineBookingPreview
              key={b.id}
              booking={b}
              scrollTo={scrollToBookingID === b.id}
            />
          ))}
        </Row>
      </Container>
    </Container>
  );
}

function AvailableTimeSlot() {
  const { userProfile } = useAuth0();
  const isBookingEnabled = useFMSettingNewOrderByPartner(
    userProfile?.partnerId || "",
    userProfile?.auth0UserId || ""
  );

  if (!userProfile) {
    return null;
  }

  return (
    <Pressable
      style={({ hovered }) => {
        return {
          height: "100%",
          backgroundColor: hovered
            ? cssVariables.backgroundLightPrimary
            : "white",
          cursor: isBookingEnabled ? "pointer" : "default",
        };
      }}
    />
  );
}

interface UnavailableTimeSlotProps {
  reason: string;
  shaded: boolean;
}

function UnavailableTimeSlot(props: UnavailableTimeSlotProps) {
  const { reason, shaded } = props;

  return (
    <Pressable
      testID={"blocked-time-slot"}
      style={{
        height: "100%",
        backgroundColor: shaded ? "#f3f2f5" : "white",
        cursor: "not-allowed",
      }}
    >
      {({ hovered }) => {
        return (
          <>
            {hovered && (
              <div
                style={{
                  position: "absolute",
                  padding: 8,
                  background: "white",
                  border: `1px solid ${cssVariables.borderColorDefault}`,
                  top: -40,
                  left: "50%",
                  transform: "translateX(-50%)",
                  borderRadius: cssVariables.radius,
                }}
              >
                <Text overflow="ellipsis" fontSize="sm">
                  {reason}
                </Text>
              </div>
            )}
          </>
        );
      }}
    </Pressable>
  );
}

interface BlockedOutTimeSlotProps {
  blockedOutDates: BlockOutDates;
}

function BlockedOutTimeSlot(props: BlockedOutTimeSlotProps) {
  const { blockedOutDates } = props;

  const [isPopoverOpen, popover] = useToggle();
  const handleHoverIn = React.useCallback(async () => {
    popover.setTrue();
  }, [popover]);

  const handleHoverOut = React.useCallback(async () => {
    popover.setFalse();
  }, [popover]);

  return (
    <>
      <Hoverable
        useWrapper
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}
        style={{ height: "100%" }}
      >
        <Popover
          isOpen={isPopoverOpen}
          onRequestClose={handleHoverOut}
          content={
            <Container
              padding={8}
              backgroundColor="white"
              borderWidth={1}
              borderColor={cssVariables.borderColorDefault}
              borderRadius={cssVariables.radius}
              boxShadow={cssVariables.shadow}
            >
              <Text overflow="ellipsis" isBold>
                Blocked Dates
              </Text>
              <Text overflow="ellipsis" fontSize="sm">
                {format(parseDay(blockedOutDates.startDate), "MMMM do, yyyy")} -{" "}
                {format(parseDay(blockedOutDates.endDate), "MMMM do, yyyy")}
              </Text>
              <Spacing height={8} />
              <Text overflow="ellipsis" fontSize="sm">
                {blockedOutDates.reason}
              </Text>
            </Container>
          }
        >
          {({ ref }) => (
            <div
              data-testid="blocked-time-slot"
              ref={ref as any}
              style={{
                height: "100%",
                backgroundColor: "#e4e4e4",
                cursor: "not-allowed",
              }}
            ></div>
          )}
        </Popover>
      </Hoverable>
    </>
  );
}

interface TimelineBookingPreviewProps {
  scrollTo: boolean;
  booking: Booking;
}

function TimelineBookingPreview(props: TimelineBookingPreviewProps) {
  const { booking, scrollTo } = props;
  const ref = React.useRef<HTMLDivElement>(null);
  const { startOfDay, totalDay } = useContext(ListViewContext);

  React.useEffect(() => {
    if (scrollTo) {
      setTimeout(() => {
        if (ref.current) {
          ref.current.scrollIntoView();
        }
      }, 50);
    }
  }, [scrollTo]);

  let left = "";
  let width = "";

  if (booking.bookingType === BookingType.Daily) {
    left = "0";
    width = "100%";
  } else {
    const startTime = parseTime(booking.time);
    const startTimeDiffFromStartOfDay = differenceInMinutes(
      startTime,
      startOfDay
    );

    const startTimeLeft = (startTimeDiffFromStartOfDay / totalDay) * 100;
    const endTime = addHours(startTime, Number(booking.duration));
    const endTimeDiffFromStartOfDay = differenceInMinutes(endTime, startOfDay);
    const endTimeLeft = (endTimeDiffFromStartOfDay / totalDay) * 100;

    left = `${startTimeLeft}%`;
    width = `${endTimeLeft - startTimeLeft}%`;
  }

  return (
    <div
      ref={ref}
      data-testid="booking-list-item"
      style={{
        position: "absolute",
        left,
        width,
        height: "100%",
      }}
    >
      <Suspense fallback={<Container expanded backgroundColor="#ffe499" />}>
        <BookingPreview includeSpaceName={false} booking={booking} />
      </Suspense>
    </div>
  );
}
